import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import { NotificationIcon, DefaultNotificationIcon } from './icons/NotificationIcons';
import './NotificationsPage.css';

const NotificationsPage = ({
  onHomeClick,
  onCategoriesClick,
  onHowItWorksClick,
  onProfileClick,
  onAccountSettingsClick,
  onForumClick,
  onLoginClick,
  onSignupClick
}) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      // Reset to page 1 when component mounts or user changes
      setPage(1);
      setNotifications([]);
      fetchNotifications(1, false);
      fetchUnreadCount();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchNotifications = async (pageNum = 1, append = false) => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      const response = await notificationAPI.getAll({ 
        per_page: 10,
        page: pageNum 
      });
      
      if (response.success && response.data) {
        // Laravel paginator returns: { data: [...], current_page, last_page, per_page, total, etc. }
        const paginationData = response.data;
        const fetchedNotifications = paginationData.data || [];
        
        // Transform API notifications to match our format
        const transformedNotifications = fetchedNotifications.map(notif => ({
          id: notif.id,
          type: notif.type,
          title: notif.title || 'Notification',
          message: notif.message || '',
          jobId: notif.data?.job_id,
          jobToken: notif.data?.share_token, // Add share_token for secure redirect
          jobTitle: notif.data?.job_title,
          reason: notif.data?.reason,
          adminName: notif.data?.admin_name,
          forumId: notif.data?.forum_id,
          schedule_id: notif.data?.schedule_id,
          template_id: notif.data?.template_id,
          applicationId: notif.data?.application_id, // For job_application notifications
          applicantId: notif.data?.applicant_id, // For job_application notifications
          applicantName: notif.data?.applicant_name, // For job_application notifications
          status: notif.data?.status, // For job_application_status notifications (shortlisted, rejected)
          rejectionReason: notif.data?.rejection_reason, // For rejected applications
          timestamp: new Date(notif.created_at),
          unread: !notif.is_read,
          notificationId: notif.id,
          readAt: notif.read_at ? new Date(notif.read_at) : null,
        }));

        if (append) {
          setNotifications(prev => [...prev, ...transformedNotifications]);
        } else {
          setNotifications(transformedNotifications);
        }

        // Server-side pagination: Use pagination metadata from backend
        if (paginationData.current_page !== undefined && paginationData.last_page !== undefined) {
          // Check if current page is less than last page
          setHasMore(paginationData.current_page < paginationData.last_page);
          setPage(paginationData.current_page);
        } else {
          // Fallback: if pagination metadata is missing, assume more exists if we got full page
          setHasMore(transformedNotifications.length === 10);
          setPage(pageNum);
        }
      }
    } catch (error) {
      console.error('[NotificationsPage] Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await notificationAPI.getUnreadCount();
      if (response.success && response.count !== undefined) {
        setUnreadCount(response.count);
      }
    } catch (error) {
      console.error('[NotificationsPage] Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.notificationId === notificationId);
    
    if (notification && notification.notificationId) {
      try {
        await notificationAPI.markAsRead(notification.notificationId);
        
        // Update local state
        setNotifications(prev => 
          prev.map(notif => 
            notif.notificationId === notificationId 
              ? { ...notif, unread: false } 
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('[NotificationsPage] Error marking notification as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, unread: false }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('[NotificationsPage] Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notificationId);
    
    // Navigate based on notification type
    if (notification.forumId) {
      navigate(`/forum/${notification.forumId}`);
    } else if (notification.type === 'job_compliance' && notification.jobToken) {
      // Navigate to job detail page using shareable token (secure, hides internal IDs)
      navigate(`/job/${notification.jobToken}`);
    } else if (notification.type === 'job_compliance' && notification.jobId) {
      // Fallback to job ID if token is not available (for backward compatibility)
      navigate(`/my-jobs?job=${notification.jobId}`);
    } else if (notification.type === 'job_application' && notification.jobToken) {
      // Navigate to my-jobs page using shareable token (secure, hides internal IDs)
      // Add tab=applications to automatically show the applications tab
      navigate(`/my-jobs?job=${notification.jobToken}&tab=applications`);
    } else if (notification.type === 'job_application' && notification.jobId) {
      // Fallback to job ID if token is not available (for backward compatibility)
      navigate(`/my-jobs?job=${notification.jobId}&tab=applications`);
    } else if (notification.type === 'job_application_status' && notification.jobToken) {
      // Navigate to applied jobs page for applicants (shortlisted or rejected status updates)
      navigate(`/applied-jobs`);
    } else if (notification.type === 'job_application_status' && notification.jobId) {
      // Fallback to job ID if token is not available
      navigate(`/applied-jobs`);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      fetchNotifications(nextPage, true);
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 8640000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatFullDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="notifications-page notifications-with-bottom-nav">
      <div className="notifications-header-desktop">
        <Header 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onLoginClick={onLoginClick || (() => navigate('/login'))}
          onSignupClick={onSignupClick || (() => navigate('/signup'))}
          isLoggedIn={isAuthenticated}
          user={user}
          onJobsClick={() => navigate('/vacancies')}
          currentPage="notifications"
        />
      </div>
      
      <div className="notifications-container">
        {!isAuthenticated ? (
          <div className="notifications-login-prompt">
            <div className="login-prompt-content">
              <div className="login-prompt-icon">
                <DefaultNotificationIcon size={64} color="#cbd5e1" />
              </div>
              <h2>Sign in to view your notifications</h2>
              <p>Please sign in to see your notifications and stay updated with important updates.</p>
              <div className="login-prompt-actions">
                <button 
                  className="btn btn-primary"
                  onClick={onLoginClick || (() => navigate('/login'))}
                >
                  Sign In
                </button>
                <button 
                  className="btn btn-outline"
                  onClick={onSignupClick || (() => navigate('/signup'))}
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="notifications-header">
              <div className="notifications-title-section">
                <h1>Notifications</h1>
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} unread</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button 
                  className="mark-all-read-button"
                  onClick={markAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>

            <div className="notifications-content">
              {loading && notifications.length === 0 ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <DefaultNotificationIcon size={64} color="#cbd5e1" />
                </div>
                <h2>No notifications yet</h2>
                <p>When you receive notifications, they'll appear here.</p>
              </div>
              ) : (
            <>
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div 
                    key={notification.notificationId || notification.id}
                    className={`notification-card ${notification.unread ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-card-content">
                      <div className="notification-icon">
                        <NotificationIcon type={notification.type} size={24} />
                      </div>
                      <div className="notification-card-body">
                        <div className="notification-card-header">
                          <h3 className="notification-card-title">{notification.title}</h3>
                          {notification.unread && <span className="unread-dot"></span>}
                        </div>
                        <p className="notification-card-message">{notification.message}</p>
                        {notification.reason && notification.type === 'job_compliance' && (
                          <div className="notification-reason">
                            <strong>Reason:</strong> {notification.reason}
                          </div>
                        )}
                        <div className="notification-card-footer">
                          <span className="notification-card-time">{formatTime(notification.timestamp)}</span>
                          {notification.readAt && (
                            <span className="notification-read-time">Read {formatTime(notification.readAt)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {hasMore && (
                <div className="load-more-section">
                  <button 
                    className="load-more-button"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="notifications-footer-desktop">
        <Footer />
      </div>
      {isAuthenticated && (
        <BottomNavigationBar 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onForumClick={onForumClick}
        />
      )}
    </div>
  );
};

export default NotificationsPage;

